import React, { useEffect, useState } from "react";
import {
  Check,
  Copy,
  CreditCard,
  Eye,
  Send,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import pixService from "@/services/pixService";
import { showError } from "@/store/errorStore";
import { PixStatusBadge } from "./PixPaymentCard";

function InfoTile({ label, children, className }) {
  return (
    <div className={cn("flex min-w-0 flex-col rounded-xl bg-surface p-3", className)}>
      <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
      <span className="mt-0.5 break-words text-[13px] font-semibold text-foreground">
        {children}
      </span>
    </div>
  );
}

/** Botão "Copiar" que mostra "Copiado" por 2 s */
function useCopy() {
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (!copiedKey) return undefined;
    const timer = setTimeout(() => setCopiedKey(null), 2000);
    return () => clearTimeout(timer);
  }, [copiedKey]);

  const copy = async (key, text) => {
    try {
      await pixService.copyToClipboard(text);
      setCopiedKey(key);
    } catch (error) {
      showError("Não foi possível copiar. Selecione o texto e copie manualmente.", "Copiar");
    }
  };

  return [copiedKey, copy];
}

/**
 * Detalhes do pagamento (docs/tela/code.html): status e valor, pedido,
 * forma, cliente, contato, datas, txid copiável e ações.
 */
const PaymentDetailsModal = ({
  open,
  onOpenChange,
  payment,
  onViewQRCode,
  onMarkReceived,
  onWhatsApp,
  onDelete,
}) => {
  const [copiedKey, copy] = useCopy();

  if (!payment) return null;

  const paid = pixService.isPaid(payment);
  const amount = pixService.getAmount(payment);
  const received = pixService.getReceivedAmount(payment);
  const payload = payment.payload || payment.pixCopiaECola;
  const whatsApp = pixService.getWhatsAppNumber(payment.contact);
  const confirmedAt = payment.confirmedAt || payment.confirmed_at;

  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md gap-4 overflow-y-auto rounded-2xl p-5">
        <DialogHeader className="flex-row items-center gap-2 space-y-0 pr-8 text-left">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-pistachio/30 text-primary">
            <CreditCard className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="font-display text-lg text-primary">
              Detalhes do Pagamento
            </DialogTitle>
            <DialogDescription className="text-xs">
              Informações completas e ações
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Status e valor */}
        <div className="flex items-center justify-between gap-3 rounded-xl bg-surface p-3.5">
          <PixStatusBadge paid={paid} className="px-3 py-1 text-xs" />
          <div className="text-right">
            <span className="block text-xs text-muted-foreground">Valor Total</span>
            <span className="font-display text-2xl font-bold tracking-tight text-primary">
              {formatCurrency(amount)}
            </span>
          </div>
        </div>

        {/* Metadados */}
        <div className="grid grid-cols-2 gap-2">
          <InfoTile label="Pedido">
            {payment.orderCode ? (
              <span className="text-primary">#{payment.orderCode}</span>
            ) : (
              <span className="text-muted-foreground">Sem pedido</span>
            )}
          </InfoTile>
          <InfoTile label="Método">{payment.pay_method || "PIX"}</InfoTile>
          <InfoTile label="Cliente">{payment.name || "-"}</InfoTile>
          <InfoTile label="Contato">
            {payment.contact ? pixService.formatPhone(payment.contact) : "-"}
          </InfoTile>
          <InfoTile label="Data de Criação" className={paid ? "" : "col-span-2"}>
            {pixService.formatDate(payment.createdAt)}
          </InfoTile>
          {paid && (
            <InfoTile label="Pago em">
              {confirmedAt ? pixService.formatDate(confirmedAt) : "-"}
            </InfoTile>
          )}
          {paid && Math.abs(received - amount) >= 0.01 && (
            <InfoTile label="Valor Recebido" className="col-span-2">
              <span className={received < amount ? "text-terra" : "text-primary"}>
                {formatCurrency(received)}
                {received < amount && ` (faltaram ${formatCurrency(amount - received)})`}
              </span>
            </InfoTile>
          )}
        </div>

        {/* ID da transação */}
        {payment.txid && (
          <div className="flex flex-col rounded-xl bg-surface p-3">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-muted-foreground">
                ID da Transação
              </span>
              <button
                type="button"
                onClick={() => copy("txid", payment.txid)}
                className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                {copiedKey === "txid" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copiedKey === "txid" ? "Copiado" : "Copiar"}
              </button>
            </div>
            <span className="break-all rounded-lg bg-card p-2 font-mono text-xs text-foreground">
              {payment.txid}
            </span>
          </div>
        )}

        {/* Ações */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          {paid ? (
            <button
              type="button"
              onClick={() => onWhatsApp(payment)}
              disabled={!whatsApp}
              title={whatsApp ? undefined : "Contato sem telefone com DDD"}
              className="col-span-2 flex h-10 items-center justify-center gap-1.5 rounded-full bg-brand-pistachio/25 px-3 text-[13px] font-semibold text-brand-dark transition-colors hover:bg-brand-pistachio/40 disabled:pointer-events-none disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              Enviar comprovante no WhatsApp
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => copy("payload", payload)}
                disabled={!payload}
                className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-brand-pistachio/25 px-3 text-[13px] font-semibold text-brand-dark transition-colors hover:bg-brand-pistachio/40 disabled:pointer-events-none disabled:opacity-50"
              >
                {copiedKey === "payload" ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copiedKey === "payload" ? "Copiado!" : "Copiar Código"}
              </button>
              <button
                type="button"
                onClick={() => {
                  close();
                  onMarkReceived(payment);
                }}
                className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-3 text-[13px] font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-brand-dark"
              >
                <Check className="h-4 w-4" />
                Marcar Pago
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              close();
              onViewQRCode(payment);
            }}
            disabled={!payment.txid}
            className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-surface-container px-3 text-[13px] font-semibold text-primary transition-colors hover:bg-surface-high disabled:opacity-50"
          >
            <Eye className="h-4 w-4" />
            Ver QR Code
          </button>
          <button
            type="button"
            onClick={() => {
              close();
              onDelete(payment);
            }}
            disabled={!payment.txid}
            className="flex h-10 items-center justify-center gap-1.5 rounded-full px-3 text-[13px] font-semibold text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            Excluir
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDetailsModal;
